import React, { useEffect, useState } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, Avatar, IconButton, Button, Alert, CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import BlockIcon from "@mui/icons-material/Block";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import { loyaltyFetch } from "../../hooks/loyaltyFetch";
import {
    catalogDeletePaths,
    fetchCatalogDeletePreview,
    formatCatalogDeleteStats,
} from "../../utils/catalogDeleteApi";
import { loyaltyApiDetail } from "../../utils/loyaltyApiMessage";

const RESOURCE_TITLES = {
    coupon_type: "Supprimer le type de coupon",
    reward: "Supprimer la récompense",
    product: "Supprimer le produit",
};

/**
 * Modale suppression catalogue : preview → confirmation → DELETE / PATCH désactiver.
 */
const CatalogDeleteDialog = ({
    open,
    resourceType,
    resource,
    brandName,
    onClose,
    onDeleted,
    onDeactivated,
}) => {
    const [preview, setPreview] = useState(null);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [previewError, setPreviewError] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [deactivating, setDeactivating] = useState(false);
    const [actionError, setActionError] = useState(null);

    const paths = resource?.id ? catalogDeletePaths(resourceType, resource.id) : null;

    useEffect(() => {
        if (!open || !paths || !brandName) {
            setPreview(null);
            setPreviewError(null);
            setActionError(null);
            return;
        }
        let cancelled = false;
        setLoadingPreview(true);
        setPreviewError(null);
        setPreview(null);
        fetchCatalogDeletePreview(paths.preview, brandName)
            .then((data) => { if (!cancelled) setPreview(data); })
            .catch((err) => { if (!cancelled) setPreviewError(err.message); })
            .finally(() => { if (!cancelled) setLoadingPreview(false); });
        return () => { cancelled = true; };
    }, [open, paths?.preview, brandName, resource?.id]);

    const handleDeactivate = async () => {
        if (!paths?.deactivate) return;
        setDeactivating(true);
        setActionError(null);
        try {
            const res = await loyaltyFetch(paths.deactivate, {
                method: "PATCH",
                headers: { "Content-Type": "application/json", "X-Brand": brandName },
                body: JSON.stringify({ active: false }),
            });
            const data = await res.json().catch(() => null);
            if (!res.ok) throw new Error(loyaltyApiDetail(data?.detail) || `Erreur HTTP ${res.status}`);
            onDeactivated?.(data);
            onClose?.();
        } catch (err) {
            setActionError(err.message);
        } finally {
            setDeactivating(false);
        }
    };

    const handleDelete = async () => {
        if (!paths?.delete) return;
        setDeleting(true);
        setActionError(null);
        try {
            const res = await loyaltyFetch(paths.delete, {
                method: "DELETE",
                headers: { "X-Brand": brandName },
            });
            const data = await res.json().catch(() => null);
            if (!res.ok) throw new Error(loyaltyApiDetail(data?.detail) || `Erreur HTTP ${res.status}`);
            onDeleted?.(data);
            onClose?.();
        } catch (err) {
            setActionError(err.message);
        } finally {
            setDeleting(false);
        }
    };

    if (!resource) return null;
    const busy = loadingPreview || deleting || deactivating;
    const previewMessage = preview?.message
        || (previewError ? null : `Confirmer la suppression définitive de « ${resource.name} » ?`);

    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth
            PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ borderBottom: "2px solid #f1f3f5", px: 3, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: "#fef2f2" }}>
                        <WarningAmberIcon sx={{ fontSize: 18, color: "#DC2626" }} />
                    </Avatar>
                    <Typography variant="h6" fontFamily="Montserrat, sans-serif" fontWeight={700} color="#1a1a2e" fontSize={16}>
                        {RESOURCE_TITLES[resourceType] || "Supprimer"}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small" disabled={busy}><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: "20px !important", px: 3, pb: 2 }}>
                <Alert severity="info" sx={{ borderRadius: 2, mb: 2, fontSize: 12 }}>
                    <strong>Désactiver</strong> (pause catalogue) : plus de nouvelles émissions, droits clients actifs inchangés.
                    <br />
                    <strong>Supprimer</strong> : retrait définitif + invalidation des actifs (ISSUED → Invalidé).
                </Alert>

                {loadingPreview && (
                    <Box display="flex" justifyContent="center" py={2}>
                        <CircularProgress size={28} />
                    </Box>
                )}

                {previewError && (
                    <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>{previewError}</Alert>
                )}

                {actionError && (
                    <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>{actionError}</Alert>
                )}

                {!loadingPreview && previewMessage && (
                    <Typography fontSize={14} color="#334155" sx={{ whiteSpace: "pre-wrap" }}>
                        {previewMessage}
                    </Typography>
                )}

                {preview?.counts && (
                    <Box mt={2} px={2} py={1} bgcolor="#f8fafc" borderRadius={2} border="1px solid #e2e8f0">
                        <Typography fontSize={11} fontWeight={700} color="text.secondary" mb={0.5}>IMPACT ESTIMÉ</Typography>
                        {Object.entries(preview.counts).map(([k, v]) => (
                            <Typography key={k} fontSize={12} color="#475569">{k}: {v}</Typography>
                        ))}
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e0e0e0", gap: 1, flexWrap: "wrap" }}>
                <Button onClick={onClose} disabled={busy} sx={{ color: "text.secondary", textTransform: "none" }}>
                    Annuler
                </Button>
                {paths?.deactivate && (
                    <Button
                        variant="outlined"
                        onClick={handleDeactivate}
                        disabled={busy || loadingPreview}
                        startIcon={deactivating ? <CircularProgress size={16} /> : <BlockIcon />}
                        sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}
                    >
                        {deactivating ? "Désactivation…" : "Désactiver"}
                    </Button>
                )}
                <Button
                    variant="contained"
                    onClick={handleDelete}
                    disabled={busy || loadingPreview || Boolean(previewError)}
                    startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
                    sx={{ bgcolor: "#DC2626", "&:hover": { bgcolor: "#b91c1c" }, textTransform: "none", borderRadius: 2, fontWeight: 600, px: 3 }}
                >
                    {deleting ? "Suppression…" : "Supprimer définitivement"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export { formatCatalogDeleteStats };
export default CatalogDeleteDialog;
